import { cn } from '@/shared/lib/cn';
import type { OperationsApartmentTurnover } from '@/shared/api';
import { AXIS_HOURS, axisPercent, type HeuteDay, type HeuteFilter } from './heuteDayModel';
import { HeuteLaneFooter } from './HeuteLaneFooter';
import { HeuteLaneTrack } from './HeuteLaneTrack';

const FILTERS: Array<[HeuteFilter, string]> = [
  ['all', 'Alle'],
  ['Departure', 'Abreisen'],
  ['InHouse', 'Im Haus'],
  ['Arrival', 'Anreisen'],
];

interface Props {
  day: HeuteDay;
  /** Zähler je Filter, wie bisher: Apartments mit einem Aufenthalt in dieser Phase. */
  counts: Record<HeuteFilter, number>;
  filter: HeuteFilter;
  onFilterChange: (filter: HeuteFilter) => void;
  /** Lage der aktuellen Uhrzeit auf der Achse; nur am heutigen Tag. */
  nowMinutes: number | null;
  checkInTime: string;
  checkOutTime: string;
  onOpen: (apartment: OperationsApartmentTurnover, trigger: HTMLElement) => void;
}

/** „Dein Tag im Haus“: je Apartment eine Bahn Abreise → Reinigung → Anreise; Klick öffnet die Details. */
export function HeuteLanes({
  day,
  counts,
  filter,
  onFilterChange,
  nowMinutes,
  checkInTime,
  checkOutTime,
  onOpen,
}: Props) {
  return (
    <section
      aria-labelledby="heute-lanes"
      className="border-hairline rounded-tile flex flex-col gap-4 border bg-white p-4 md:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <h2 id="heute-lanes" className="font-serif text-xl font-bold">
            Dein Tag im Haus
          </h2>
          <p className="text-ink-soft text-xs">
            Abreise bis {checkOutTime.slice(0, 5)} · Reinigung · Anreise ab{' '}
            {checkInTime.slice(0, 5)} · Klick auf eine Bahn öffnet die Tagesdetails
          </p>
        </div>
        <div role="group" aria-label="Filter" className="bg-paper rounded-pill flex gap-1 p-1">
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => onFilterChange(value)}
              className={cn(
                'rounded-pill h-9 px-3.5 text-[13px] font-bold',
                filter === value ? 'bg-ink text-white' : 'text-ink',
              )}
            >
              {label} {counts[value]}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-[minmax(84px,128px)_1fr] gap-x-3 gap-y-2">
        <span />
        <div className="text-ink-faint relative h-4 font-mono text-[10px]">
          {AXIS_HOURS.map((hour) => (
            <span
              key={hour}
              className="absolute -translate-x-1/2"
              style={{ left: `${axisPercent(hour * 60)}%` }}
            >
              {String(hour).padStart(2, '0')}
            </span>
          ))}
        </div>
        {day.lanes.length === 0 && (
          <p className="text-ink-soft col-span-2 flex flex-wrap items-center gap-3 py-4 text-sm">
            {filter === 'all'
              ? 'An diesem Tag kein Wechsel, keine An- oder Abreise und keine Reinigung.'
              : 'Für diesen Filter gibt es keine Bahn.'}
            {filter !== 'all' && (
              <button
                type="button"
                onClick={() => onFilterChange('all')}
                className="text-forest font-bold"
              >
                Alle zeigen
              </button>
            )}
          </p>
        )}
        {day.lanes.map((lane) => (
          <button
            key={lane.apartment.propertyId}
            type="button"
            aria-label={`Tagesdetails ${lane.apartment.adminDisplayName}`}
            aria-describedby={`lane-${lane.apartment.propertyId}`}
            onClick={(event) => onOpen(lane.apartment, event.currentTarget)}
            className="hover:bg-paper rounded-control col-span-2 grid min-h-14 grid-cols-subgrid items-center text-left"
          >
            <span id={`lane-${lane.apartment.propertyId}`} className="sr-only">
              {[lane.kind, ...lane.segments.map((segment) => segment.label)].join(', ')}
            </span>
            <span className="flex min-w-0 flex-col pl-1">
              <span className="truncate font-serif text-base font-bold">
                {lane.apartment.adminDisplayName}
              </span>
              <span className="text-ink-soft text-xs">
                {lane.kind}
                {lane.apartment.isManuallyBlocked ? ' · manuell gesperrt' : ''}
              </span>
            </span>
            <HeuteLaneTrack segments={lane.segments} nowMinutes={nowMinutes} />
          </button>
        ))}
      </div>
      <HeuteLaneFooter day={day} onOpen={onOpen} />
    </section>
  );
}
